import type {
  DatasetInstanceConfig,
  SelectionListSelectedItem,
} from './data-builder.store';

export type UoaConsensus = {
  unitOfAnalysis: string;
  unitOfAnalysisSelections: SelectionListSelectedItem[];
};

/**
 * Compute consensus UOA from a set of dataset instances.
 * Ignores instances with null unitOfAnalysis.
 * Returns the agreed-upon UOA type + selections from the first match, or null if no consensus.
 */
export function getUnitOfAnalysisConsensus(
  configByInstance: Record<string, DatasetInstanceConfig>,
  instanceIds: string[]
): UoaConsensus | null {
  const nonNullConfigs = instanceIds
    .map((id) => configByInstance[id])
    .filter(
      (config): config is DatasetInstanceConfig =>
        config !== undefined && config.unitOfAnalysis !== null
    );

  const firstConfig = nonNullConfigs[0];
  if (!firstConfig) {
    return null;
  }

  const firstType = firstConfig.unitOfAnalysis;
  const allAgree = nonNullConfigs.every(
    (config) => config.unitOfAnalysis === firstType
  );

  if (!allAgree) {
    return null;
  }

  return {
    unitOfAnalysis: firstType as string,
    unitOfAnalysisSelections: firstConfig.unitOfAnalysisSelections,
  };
}

/**
 * Given a consensus and the available UOA option values for a target instance,
 * return the consensus as a patch if the target supports it, or null otherwise.
 */
export function getUoaDefaultForInstance(
  consensus: UoaConsensus,
  availableUoaOptionValues: string[]
): UoaConsensus | null {
  if (availableUoaOptionValues.includes(consensus.unitOfAnalysis)) {
    return consensus;
  }
  return null;
}
